import { initializeApp, type FirebaseApp } from 'firebase/app'
import { getAuth, type Auth } from 'firebase/auth'
import { getMessaging, isSupported } from 'firebase/messaging'

let firebaseApp: FirebaseApp | null = null
let firebaseAuth: Auth | null = null

export async function initFirebase(): Promise<void> {
  if (firebaseApp) return

  let config: Record<string, string>

  try {
    const res = await fetch('/__/firebase/init.json')
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    config = await res.json()
    // console.info('[Firebase] /__/firebase/init.json から設定を読み込みました')
  } catch {
    // console.info('[Firebase] 環境変数から設定を読み込みます (ローカル開発)')
    config = {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY ?? '',
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN ?? '',
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID ?? '',
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID ?? '',
      appId: process.env.REACT_APP_FIREBASE_APP_ID ?? '',
    }
  }

  firebaseApp = initializeApp(config)
  firebaseAuth = getAuth(firebaseApp)
  firebaseAuth.languageCode = 'ja'
}

export function getFirebaseAuth(): Auth {
  if (!firebaseAuth) throw new Error('[Firebase] 未初期化です。initFirebase() を先に呼び出してください。')
  return firebaseAuth
}

// 後方互換: auth は initFirebase() 完了後にのみ使用可能
export const auth: Auth = new Proxy({} as Auth, {
  get(target, prop: string | symbol) {
    if (!firebaseAuth) throw new Error('[Firebase] 未初期化です。initFirebase() を先に呼び出してください。')
    return (firebaseAuth as unknown as Record<string | symbol, unknown>)[prop]
  },
  set(target, prop: string | symbol, value) {
    if (!firebaseAuth) throw new Error('[Firebase] 未初期化です。initFirebase() を先に呼び出してください。')
    ;(firebaseAuth as unknown as Record<string | symbol, unknown>)[prop] = value
    return true
  },
})

const isFcmConfigured = () =>
  Boolean(firebaseApp) &&
  Boolean((firebaseApp as unknown as { options: Record<string, string> })?.options?.appId) &&
  Boolean((firebaseApp as unknown as { options: Record<string, string> })?.options?.messagingSenderId)


export const getMessagingIfSupported = async () => {
  if (!firebaseApp || !isFcmConfigured()) {
    // console.info('[FCM] appId / messagingSenderId が未設定のため Messaging をスキップします')
    return null
  }
  const supported = await isSupported()
  return supported ? getMessaging(firebaseApp) : null
}

export const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || undefined
